// Imports
import React, { useState, useEffect, useLayoutEffect } from "react";
import axios from "axios";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import GlobalStyle from './GlobalStyle';
import { FaTrash, FaEdit, FaFileExport } from "react-icons/fa";

const columnWidths = {
  orderId: "250px",
  causale: "220px"
};
const PageContainer = styled.div`
  background-color: #fffdf3;
  padding: 5px;

  @media (max-width: 1024px) {
    padding: 10px;
  }

  @media (max-width: 768px) {
    padding: 10px 5px;
  }
`;
const Input = styled.input`
  width: 100%;
  padding: 10px;
  margin: 6px 0;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  @media (max-width: 768px) {
    font-size: 12px;
    padding: 6px;
  }
`;
const Button = styled.button`
  padding: 6px 10px;
  background: #28a745;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 6px;
  &:hover {
    background: #218838;
  }
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
  @media (max-width: 768px) {
    padding: 4px 8px;
    font-size: 12px;
    gap: 4px;
  }
`;

const DangerButton = styled(Button)`
  background: #dc3545;
  &:hover {
    background: #c82333;
  }
`;
const FiltersContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  width: 100%;
  margin-bottom: 10px;
  & > * {
    flex: 1 1 160px;
    min-width: 120px;
  }
  @media (max-width: 768px) {
    gap: 8px;
    & > * {
      flex: 1 1 140px;
      min-width: 100px;
    }
  }
`;
const TableWrapper = styled.div`
  overflow-x: auto;
  width: 100%;
`;
const StyledTable = styled.table`
  width: 100%;
  min-width: 600px;
  border-collapse: collapse;
  th, td {
    border: 1px solid #ddd;
    padding: 10px;
    text-align: left;
  }
  th {
    background-color: #f9f9f9;
    font-weight: 600;
  }
  tr:nth-child(even) {
    background-color: #fefefe;
  }
`;

// Main function
const OrdersEdit = () => {
 const [orders, setOrders] = useState([]);
 const [filters, setFilters] = useState({});
 const [currentPage, setCurrentPage] = useState(1);
 const ORDERS_PER_PAGE = 20;
 const navigate = useNavigate();

 useEffect(() => {
  const fetchOrders = async () => {
   try {
    const res = await axios.get("/api/bollereso");
    const data = Array.isArray(res.data)
    ? res.data.map(order => ({
    ...order,
    products: order.products ? (typeof order.products === 'string' ? JSON.parse(order.products) : order.products) : [],
    causale: order.CAUSALE || order.causale || ""
   }))
    : [];
    setOrders(data);
   } catch (err) {
     console.error("Errore caricamento bolle:", err);
   }
  };
    fetchOrders();
  }, []);

 // 
 const handleDeleteOrder = async (orderId) => {
  if (!window.confirm("Sei sicuro di voler eliminare questa bolla di reso?")) return;
   try {
    await axios.delete("/api/deleteBollareso", { data: { orderId } });
    setOrders(prev => prev.filter(o => o.orderId !== orderId));
   } catch (err) {
      console.error("Errore eliminazione bolla reso:", err.response?.data || err);
      alert("Errore durante l'eliminazione della bolla di reso.");
    }
  };

  const handleExport = async (orderId) => {
    if (!window.confirm("Esportare questa bolla di reso?")) return;
    try {
      await axios.post("/api/creaBollareso", { orderId });
      alert("Bolla esportata con successo.");
    } catch (err) {
      console.error("Errore esportazione:", err);
      alert("Errore durante l'esportazione.");
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const filteredOrders = orders.filter(order =>
    ["cliente", "orderDate", "causale"].every(key =>
      filters[key] ? order[key]?.toString().toLowerCase().includes(filters[key].toLowerCase()) : true
    )
  );

  const paginatedOrders = filteredOrders.slice((currentPage - 1) * ORDERS_PER_PAGE, currentPage * ORDERS_PER_PAGE);
  const totalPages = Math.ceil(filteredOrders.length / ORDERS_PER_PAGE);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  return (
    <>
      <GlobalStyle />
      <PageContainer>
        <h4>Gestione Bolle di Reso</h4>

        <FiltersContainer>
          <Input placeholder="Filtra per cliente / data" value={filters.cliente || ""} onChange={(e) => handleFilterChange("cliente", e.target.value)} />
          <Input placeholder="Filtra per causale" value={filters.causale || ""} onChange={(e) => handleFilterChange("causale", e.target.value)} />
        </FiltersContainer>

        <TableWrapper>
          <StyledTable>
            <thead>
              <tr>
                <th style={{ width: columnWidths.orderId }}>Ordine</th>
                <th style={{ width: columnWidths.causale }}>Causale</th>
                <th>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {paginatedOrders.map((order, index) => (
                <tr key={index}>
                  <td>{order.orderId}</td>
                  <td>{order.causale}</td>
                  <td>
                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                      <Button onClick={() => navigate(`/bollereso-edit-products/${order.orderId}`)}>
                        <FaEdit /> <span className="button-label">Modifica</span>
                      </Button>
                      <DangerButton onClick={() => handleExport(order.orderId)}>
                        <FaFileExport /> <span className="button-label">Esporta</span>
                      </DangerButton>
                      <DangerButton onClick={() => handleDeleteOrder(order.orderId)}>
                        <FaTrash />
                      </DangerButton>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </StyledTable>
        </TableWrapper>

        <div style={{ marginTop: "20px", display: "flex", justifyContent: "center", gap: "10px" }}>
          <Button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}>← Pagina precedente</Button>
          {[...Array(totalPages)].map((_, i) => (
            <Button key={i} onClick={() => goToPage(i + 1)} style={{ backgroundColor: currentPage === i + 1 ? "#007bff" : "#28a745", fontWeight: currentPage === i + 1 ? "bold" : "normal" }}>{i + 1}</Button>
          ))}
          <Button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}>Pagina successiva →</Button>
        </div>
      </PageContainer>
    </>
  );
};

export default OrdersEdit;
